import * as THREE from 'three';

/**
 * Dispose a texture AND null out its CPU-side source data.
 *
 * `texture.dispose()` releases the WebGL side (GPU texture), but the parsed
 * CPU-side data (Float32Array for HDR/EXR, ImageBitmap for PNG/JPG) in
 * `texture.source.data` stays alive until JS GC runs — which on iOS Safari
 * can lag by seconds. For a 4K HDR EXR that's 128 MB retained per texture.
 * Nulling the source ref lets JSC reclaim immediately.
 */
const disposeTextureFully = (tex: THREE.Texture | null | undefined): void => {
  if (!tex) return;
  if (tex.source) {
    tex.source.data = null;
  }
  tex.dispose();
};

/**
 * Dispose all texture slots of a material, including CPU-side data.
 *
 * Textures on materials are NOT freed by `material.dispose()` alone — each
 * texture must be explicitly disposed. Without this, 4K HDR/EXR maps and
 * PBR texture atlases linger in GPU memory across scene teardowns (critical
 * on iOS, where the WebGL heap is tight and lazy GC leads to OOM-kills).
 *
 * **Iteration strategy:** walks ALL enumerable fields of the material and
 * disposes anything with `.isTexture === true`. Catches every `*Map` field
 * defined by Three.js base + PBR materials (`map`, `normalMap`,
 * `roughnessMap`, `metalnessMap`, `aoMap`, `emissiveMap`, `envMap`,
 * `bumpMap`, `displacementMap`, `lightMap`, `alphaMap`, `specularMap`,
 * `gradientMap`, `matcap`, `clearcoatMap`, `clearcoatNormalMap`,
 * `clearcoatRoughnessMap`, `iridescenceMap`, `iridescenceThicknessMap`,
 * `sheenColorMap`, `sheenRoughnessMap`, `transmissionMap`, `thicknessMap`,
 * `specularIntensityMap`, `specularColorMap`, `anisotropyMap`,
 * `attenuationMap`, …) plus any user-attached textures on the instance.
 *
 * Earlier per-PBR-field hand-coded enumeration missed
 * `MeshPhysicalMaterial`-specific fields (transmissionMap, clearcoat*,
 * etc), causing per-swap CPU heap leaks on artifact GLBs that use glass /
 * metallic-clearcoat materials. Iteration-based approach is future-proof
 * and one place to maintain.
 */
export const disposeMaterialTextures = (mat: THREE.Material): void => {
  // Iterate every enumerable property on the material instance. Includes
  // base class `*Map` fields (map, lightMap, aoMap, emissiveMap, ...),
  // PBR fields (normal/roughness/metalness/env), MeshPhysicalMaterial-only
  // fields (transmission/clearcoat/sheen/iridescence/anisotropy/...), and
  // any user-attached `someCustomTexture: THREE.Texture` instance fields.
  for (const key in mat) {
    const value = (mat as unknown as Record<string, unknown>)[key];
    if (value && (value as { isTexture?: boolean }).isTexture === true) {
      disposeTextureFully(value as THREE.Texture);
    }
  }
};

/**
 * Walk a scene (or sub-tree) and dispose all renderable primitive
 * geometries, materials, and their textures. Safety net for helper
 * primitives that aren't tracked individually (dev visuals, collider
 * visualizers, overlays, particle systems).
 *
 * NB: `instanceof THREE.Mesh` alone misses `Points`, `Line`, `LineSegments`,
 * `Sprite` — they are parallel Object3D subclasses, not Mesh descendants.
 * Their materials + textures leak into the shared WebGLRenderer's program
 * cache forever, which is a primary iOS OOM trigger across scene transitions.
 * Duck-typing via the Three.js `is*` flags catches all current renderable
 * primitive types and stays compatible with future additions.
 *
 * Does NOT remove nodes from the scene — call before `renderer.dispose()` so
 * the subsequent `forceContextLoss()` finds a clean GPU state.
 */
export const disposeSceneGraph = (root: THREE.Object3D): void => {
  type RenderablePrimitive = THREE.Object3D & {
    isMesh?: boolean;
    isPoints?: boolean;
    isLine?: boolean;
    isSprite?: boolean;
    geometry?: THREE.BufferGeometry;
    material?: THREE.Material | THREE.Material[];
  };
  root.traverse((raw) => {
    const obj = raw as RenderablePrimitive;
    if (!obj.isMesh && !obj.isPoints && !obj.isLine && !obj.isSprite) return;
    obj.geometry?.dispose();
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const mat of materials) {
      if (mat && typeof mat.dispose === 'function') {
        disposeMaterialTextures(mat);
        mat.dispose();
      }
    }
  });
};
